import React, { useState, useEffect } from 'react';
import { View, TouchableOpacity, NativeSyntheticEvent, TextInputKeyPressEventData } from 'react-native';
import { translate } from '../../../services/translate';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import MyAppText from '../../../components/MyAppText';
import { Pagination } from '../../../components/Pagination';
import { dispatchService } from '../../../services/central-api/dispatch';
import MyDataTable from '../../../components/MyDataTable';
import { faUser, IconDefinition } from '@fortawesome/free-solid-svg-icons';
import Toast from 'react-native-toast-message';
import { ClientError } from '../../../services/central-api/base-service';
import { NavigationProp, RouteProp, useIsFocused, useNavigation, useRoute } from '@react-navigation/native';
import { DispatchParamList } from '../../../typings/Params';
import moment from 'moment';
import UnitsFilter from './UnitsFilter';
import useFilters from '../../../components/Filter/hooks/useFilters';
import FilterInput from '../../../components/Filter/components/FilterInput';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import CustomButton from '../../../components/CustomButton';
import FinishTeamModal from './FinishTeamModal';
import UnitPauseModal from './UnitPauseModal';
import { useAuth } from '../../../context/Auth';
import Tooltip from '../../../components/Tooltip';
import { minutesToText } from '../../../services/time-helpers';
import DropdownButton from '../../../components/DropdownButton';
import { MaskService } from 'react-native-masked-text';
import { useQuery } from '@tanstack/react-query';
import Container from '../../../components/Container';
import Header from '../../../components/Header';
import Card from '../../../components/Card';
import DataTableHeader from '../../../components/DataTableHeader';
import DataTableContainer from '../../../components/DataTableContainer';
import Badge, { BadgeColor } from '../../../components/Badge';
import PulsingText from '../../../components/PulsingText';
import { faAlarmExclamation } from '@fortawesome/pro-regular-svg-icons';
import { openInNewTab } from '../../../services/navigation-assistant';
import { dispatchModule } from '../../../routes/dispatch';
import { useCurrentApp } from '../../../context/CurrentApp';

const ONLINE_AGENT_TIME_MINUTES = 15;
export default function Units() {
    const { styles, theme } = useStyles(styleSheet);
    const route = useRoute<RouteProp<DispatchParamList, 'Units'>>();
    const { user: authenticatedUser } = useAuth();
    const navigation = useNavigation<NavigationProp<DispatchParamList>>();
    const currentApp = useCurrentApp();

    const [earlyFinishModal, setEarlyFinishModal] = useState(false);
    const [pauseModal, setPauseModal] = useState(false);
    const [selectedUnit, setSelectedUnit] = useState<PaginatedUnit>();

    const [assignments, setAssignments] = useState<DropdownResource[]>([]);

    const [filters, setFilters] = useFilters<DispatchParamList['Units']>({
        ...route.params,
        page: Math.max(route.params.page - 1, 0),
        limit: route.params.limit ?? 25,
    });

    const [teamFilter, setTeamFilter] = useState(filters.teamFilter ?? '');

    const isFocused = useIsFocused();
    const queryResult = useQuery({
        queryKey: ['getPaginatedUnits', filters],
        queryFn: () => dispatchService.getPaginatedUnits(filters),
        enabled: !!authenticatedUser && isFocused,
    });

    const isEnabled = !!authenticatedUser;
    const isLoading = isEnabled && queryResult.isFetching;

    const { data, refetch } = queryResult;

    function handleKeyDown(e: NativeSyntheticEvent<TextInputKeyPressEventData>) {
        if (e.nativeEvent.key == 'Enter') {
            navigation.setParams({
                ...filters,
                teamFilter: teamFilter !== '' ? teamFilter : undefined,
                page: 1,
            });
            setFilters(old => ({
                ...old,
                teamFilter: teamFilter !== '' ? teamFilter : undefined,
                page: 0,
            }));
        }
    }

    useEffect(() => {
        if (isFocused) {
            refetch();
            getAssignments();
        }
    }, [filters, isFocused]);

    async function getAssignments() {
        try {
            const assignments = await dispatchService.getSimplifiedAssignments();
            setAssignments(assignments);
        } catch (err) {
            console.error(err);
        }
    }

    const tooltipContent = (user: SimplifiedUserForUnit) => {
        if (user.agentPosition && moment(user.agentPosition.positionUpdatedAt) > moment().subtract(ONLINE_AGENT_TIME_MINUTES, 'minutes')) {
            return translate('activeAndLocation');
        } else {
            return translate('activeAndNoLocation');
        }
    };

    const renderUnitContent = (unit: PaginatedUnit) => {
        const renderUnitUsers = () => {
            if (!unit.UnitUsers || unit.UnitUsers.length === 0) {
                return null;
            }
            return unit.UnitUsers.map((user, i) => {
                const assignment = assignments.find((assignment) => assignment.id === user.UnitUser.assignmentId);
                const userStatus = unit.users.findIndex(x => x.id === user.id) !== -1 ? translate('active') : translate('notActive');
                const formattedPhone = user.phone
                    ? MaskService.toMask('cel-phone', user.phone, {
                        maskType: 'BRL',
                        withDDD: true,
                        dddMask: '(99) '
                    })
                    : user.phone;
                const formattedDocument = user.document
                    ? MaskService.toMask('cpf', user.document)
                    : user.document;

                return (
                    <Tooltip key={i} tooltipDirection='right' style={{ width: 300, left: '35%' }} tooltipContent={tooltipContent(user)}>
                        <View style={{ flexDirection: 'row', alignItems: 'center', columnGap: 10 }}>
                            <FontAwesomeIcon
                                icon={faUser}
                                fontSize={16}
                                color={
                                    user.agentPosition &&
                                        moment(user.agentPosition.positionUpdatedAt) >
                                        moment().subtract(ONLINE_AGENT_TIME_MINUTES, 'minutes')
                                        ? 'green'
                                        : 'orange'
                                }
                            />
                            <MyAppText>{user.warName || user.name}{' / '}{user.role?.name}</MyAppText>
                            {assignment && <MyAppText>{'/ '}{assignment.name}</MyAppText>}
                            <View style={styles.separator} />
                            <MyAppText>{translate('phone')}: {formattedPhone}</MyAppText>
                            <View style={styles.separator} />
                            <MyAppText>{translate('document')}: {formattedDocument}</MyAppText>
                            <MyAppText style={{ fontSize: 12, color: theme.colors.iconColor }} >({userStatus})</MyAppText>
                        </View>
                    </Tooltip>
                );
            });
        };

        return (
            <View style={{ paddingVertical: 10, paddingLeft: 50, flexDirection: 'column', rowGap: 10 }}>
                {renderUnitUsers()}
                {unit.description && unit.description.length && <MyAppText>{`${translate('description')}: ${unit.description}`}</MyAppText>}
                {unit.PivotOccurrenceUnits && unit.PivotOccurrenceUnits.length > 0
                    ? <MyAppText>{unit.PivotOccurrenceUnits.length > 1 ? translate('activeOccurrences') : translate('activeOccurrence')}{': '}{unit.PivotOccurrenceUnits.map((pivotOccurrenceUnit, index) => {
                        return <>
                            <TouchableOpacity key={pivotOccurrenceUnit.occurrenceId} onPress={() => {
                                openInNewTab('Dispatch', {
                                    screen: 'DispatchOccurrenceDetail',
                                    params: {
                                        occurrenceId: pivotOccurrenceUnit.occurrenceId,
                                    }
                                });
                            }} >
                                <MyAppText style={{ textDecorationLine: 'underline' }}>
                                    {pivotOccurrenceUnit.occurrenceId}
                                </MyAppText>
                            </TouchableOpacity>
                            {unit.PivotOccurrenceUnits && index < unit.PivotOccurrenceUnits.length - 1 ? <MyAppText>{', '}</MyAppText> : null}
                        </>;
                    })}</MyAppText>
                    : null}
            </View>
        );
    };

    function renderStatus(unit: PaginatedUnit) {
        let customStyle: BadgeColor = 'green';
        let customName = 'available';
        let tooltip = 'active';
        let displacedExtraContent = undefined;

        if (unit.definitiveStart && unit.definitiveFinish) {
            customStyle = 'gray';
            customName = 'finished_f';
            tooltip = 'finished_f';
        } else if (unit.paused) {
            customStyle = 'purple';
            customName = 'femalePaused';
            tooltip = 'operationalPause';
        } else if (unit.displaced) {
            customStyle = 'blue';
            customName = 'displace';
            tooltip = 'displaced';
            const displacedOccurrence = unit.PivotOccurrenceUnits?.find(x => x.displaced);
            displacedExtraContent = displacedOccurrence && displacedOccurrence.displaceReason ?
                `${displacedOccurrence.displaceReason.name}${displacedOccurrence?.policeStation ? ' - ' + displacedOccurrence.policeStation.name : ''}`
                : undefined;
        } else if (unit.definitiveStart && !unit.definitiveFinish && !unit.displaced && unit.PivotOccurrenceUnits && unit.PivotOccurrenceUnits.length > 0) {
            customStyle = 'teal';
            customName = 'inService';
            tooltip = 'inServiceTooltip';
        } else if (!unit.definitiveStart && !unit.definitiveFinish && moment(unit.estimatedStart).toDate().getTime() > moment().toDate().getTime()) {
            customStyle = 'yellow';
            customName = 'waiting';
            tooltip = 'waitingActivation';
        } else if (!unit.definitiveStart && !unit.definitiveFinish && moment(unit.estimatedStart).toDate().getTime() < moment().toDate().getTime()) {
            customStyle = 'orange';
            customName = 'overdue';
            tooltip = 'waitingActivationBeforeSchedule';
        } else if (unit.definitiveStart && !unit.definitiveFinish && moment(unit.estimatedFinish).toDate().getTime() < moment().toDate().getTime()) {
            customStyle = 'red';
            customName = 'femaleExceeded';
            tooltip = 'activeAfterSchedule';
        }

        const additionalContent =
            tooltip === 'operationalPause' && unit.unitPauseReason ? unit.unitPauseReason.name :
                tooltip == 'displaced' && displacedExtraContent ? displacedExtraContent : '';

        return <View style={{ justifyContent: 'center', alignItems: 'flex-start', zIndex: 1 }}>
            <Tooltip
                tooltipContent={<><View>
                    {translate(tooltip)}</View><View>{additionalContent ? `(${additionalContent})` : ''}</View>
                </>}
                tooltipDirection='right'
            >
                <Badge
                    mode='SQUARE'
                    color={customStyle}
                >{translate(customName)}</Badge>
            </Tooltip>
        </View>;
    }

    async function unpauseUnit(unit: PaginatedUnit) {
        try {
            if (unit.paused) {
                await dispatchService.pauseUnpauseUnit(unit.id, 'unpause');
                Toast.show({
                    type: 'sentinelxSuccess',
                    text1: translate('ActionSuccessfully'),
                });
                refetch();
            }
        } catch (err) {
            if (err instanceof ClientError) {
                return Toast.show({
                    type: 'sentinelxError',
                    text1: translate(err.message),
                });
            }
            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        }
    }

    function isLate(unit: PaginatedUnit) {
        if (unit.paused && unit.unitPauseReason && unit.unitPauseReason.pauseMinutes) {
            return moment().diff(unit.pause_start, 'minutes') > unit.unitPauseReason.pauseMinutes;
        }

        return false;
    }

    function getTimeInPause(unit: PaginatedUnit) {
        const minutesInPause = moment().diff(unit.pause_start, 'minutes');
        return minutesToText(minutesInPause);
    }

    function getOverdueTime(unit: PaginatedUnit) {
        const overdueMinutes = moment().diff(unit.pause_start, 'minutes') - (unit.unitPauseReason?.pauseMinutes ?? 0);
        return minutesToText(overdueMinutes);
    }

    return <Container>
        <Header
            title={translate('teams')}
            actions={
                <CustomButton text={translate('register')} onPress={() => {
                    setSelectedUnit(undefined);
                    navigation.navigate('UnitForm');
                }} />
            }
        />

        <Card>
            <DataTableHeader
                zIndex={2}
                title={translate('registeredTeams')}
                filter={<UnitsFilter
                    authenticatedUser={authenticatedUser}
                    filters={filters}
                    setFilters={setFilters}
                    teamFilter={teamFilter}
                    setTeamFilter={setTeamFilter} />}
                filterInput={<FilterInput
                    value={teamFilter}
                    placeholder={translate('typeToSearch')}
                    onChange={setTeamFilter}
                    onKeyPress={handleKeyDown}
                />}
            />

            <DataTableContainer
                dataTable={<MyDataTable
                    columns={[{
                        name: translate('status'),
                        cell: row => renderStatus(row)
                    }, {
                        name: translate('name'),
                        selector: row => row.unitName?.name || '',
                        grow: 1,
                        wrap: true
                    }, {
                        name: translate('overdue'),
                        cell: row =>
                            isLate(row) &&
                            <Tooltip
                                tooltipDirection='right'
                                tooltipContent={translate('unitOverdueMessage', {
                                    pauseReason: row.unitPauseReason?.name,
                                    timeInPause: getTimeInPause(row),
                                    overdueTime: getOverdueTime(row),
                                })}
                            >
                                <PulsingText
                                    fromColor={theme.colors.neutral[300]}
                                    toColor={theme.colors.offline.fontColor}
                                >
                                    <FontAwesomeIcon icon={faAlarmExclamation as IconDefinition} size='lg' />
                                </PulsingText>
                            </Tooltip>,
                        grow: 0,
                        wrap: true,
                        center: true,
                    },
                    {
                        name: translate('actingBody'),
                        selector: row => row.actingBodyUnit?.actingBody?.name || '',
                        wrap: true
                    },
                    {
                        name: translate('command'),
                        selector: row => row.actingBodyCommand?.name || '',
                        wrap: true
                    },
                    {
                        name: translate('unit'),
                        selector: row => row.actingBodyUnit?.name || '',
                        wrap: true
                    },
                    {
                        name: translate('estimatedDateStart'),
                        selector: row => moment(row.estimatedStart).format('DD/MM/YYYY HH:mm'),
                        wrap: true,
                        maxWidth: '200px'
                    },
                    {
                        name: translate('estimatedDateFinish'),
                        selector: row => moment(row.estimatedFinish).format('DD/MM/YYYY HH:mm'),
                        wrap: true,
                        maxWidth: '200px'
                    },
                    {
                        button: true,
                        cell: row => {
                            return <DropdownButton
                                size={200}
                                actions={[{
                                    content: !row.definitiveFinish ? translate('editInformation') : translate('checkInformation'),
                                    onPress: () => {
                                        navigation.navigate('UnitForm', { id: row.id });
                                    },
                                }, {
                                    content: translate('pause'),
                                    onPress: () => {
                                        setSelectedUnit(row);
                                        setPauseModal(true);
                                    },
                                    omit: (!authenticatedUser?.isAdmin &&
                                        !authenticatedUser?.permissions.dispatch_pause_units)
                                        || row.displaced
                                        || !(row.definitiveStart && !row.definitiveFinish)
                                        || row.paused
                                }, {
                                    content: translate('unpause'),
                                    onPress: async () => {
                                        await unpauseUnit(row);
                                    },
                                    omit: (!authenticatedUser?.isAdmin &&
                                        !authenticatedUser?.permissions.dispatch_pause_units)
                                        || row.displaced
                                        || !(row.definitiveStart && !row.definitiveFinish)
                                        || !row.paused
                                }, {
                                    content: translate('goToTeamName'),
                                    onPress: async () => {
                                        if (row.unitNameId) {
                                            navigation.navigate('TeamName', { id: row.unitNameId });
                                        }
                                    },
                                    omit: !dispatchModule.screens.TeamName.canAccess(authenticatedUser, currentApp),
                                }, {
                                    content: translate('finishTeamMenu'),
                                    variant: 'warning',
                                    omit: Boolean(row.definitiveFinish),
                                    onPress: () => {
                                        setSelectedUnit(row);
                                        setEarlyFinishModal(true);
                                    },
                                }]}
                            />;
                        }
                    }]}
                    data={data?.rows ?? []}
                    progressPending={isLoading}
                    expandableRowsComponent={({ data }) => renderUnitContent(data)}
                    expandableRows={!filters.status?.includes('finished')}
                />}
                pagination={<Pagination
                    currentPage={filters.page}
                    totalItems={data?.count ?? 0}
                    setPage={page => {
                        setFilters(old => ({
                            ...old,
                            page
                        }));

                        navigation.setParams({
                            ...route.params,
                            page: page + 1
                        });
                    }}
                    pageSize={filters.limit}
                    setPageSize={pageSize => {
                        setFilters(old => ({
                            ...old,
                            limit: pageSize,
                            page: 0,
                        }));

                        navigation.setParams({
                            ...route.params,
                            limit: pageSize,
                            page: 1,
                        });
                    }}
                />}
            />
        </Card>

        <FinishTeamModal
            isModalVisible={earlyFinishModal}
            setModalVisible={setEarlyFinishModal}
            unit={{
                id: selectedUnit?.id || 0,
                unitName: selectedUnit?.unitName?.name || '',
            }}
            onClose={() => {
                setSelectedUnit(undefined);
                setEarlyFinishModal(false);
                refetch();
            }}
        />
        {selectedUnit && <UnitPauseModal
            isModalVisible={pauseModal}
            setModalVisible={setPauseModal}
            unit={selectedUnit}
            onClose={() => {
                setSelectedUnit(undefined);
                setPauseModal(false);
                refetch();
            }}
        />}
    </Container>;
}

const styleSheet = createStyleSheet((theme) => ({
    circle: {
        width: 15,
        height: 15,
        borderRadius: 7.5,
    },
    green: {
        backgroundColor: 'green',
    },
    yellow: {
        backgroundColor: '#FFD700',
    },
    red: {
        backgroundColor: 'red',
    },
    orange: {
        backgroundColor: 'orange'
    },
    grey: {
        backgroundColor: 'grey'
    },
    purple: {
        backgroundColor: 'purple'
    },
    turquoise: {
        backgroundColor: 'turquoise'
    },
    status: {
        flexDirection: 'row',
        alignItems: 'center',
        columnGap: 5
    },
    pauseButton: {
        flexDirection: 'row',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: theme.colors.primaryButton.background,
        borderRadius: 2,
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
        gap: 5,
        padding: 5,
        minWidth: 100,
        maxWidth: 100,
        maxHeight: 35
    },
    pauseText: {
        color: theme.colors.container.background,
        fontSize: 12,
    },
    separator: {
        height: 1,
        width: 10,
        backgroundColor: 'white',
    }
}));
